import axios     from "axios";
import store     from '../store/index.js'
import authError from "@/errors/authError";
import apiError  from "@/errors/apiError";


const apiService = ( authorization=true ) => {

	let baseUrl = process.env.VUE_APP_SIGNATURES_API_URL;
	let appendUrl =  process.env.VUE_APP_SIGNATURES_API_URL_PARAMS;



	const defaultOptions = {};

	const urlBuild = function( urlPath ) {
		if(urlPath.substr(0,4)==='http') {
			return urlPath;
		}

		let cleanUrlPath = urlPath.replace( /^\/+|\/+$/g, '' );
		let append       = '';
		if( appendUrl!==null && appendUrl!=='' ) {
			append = '?';
			if( cleanUrlPath.indexOf( '?' ) > -1 ) {
				append = '&';
			}
			append += appendUrl;
		}

		return baseUrl + cleanUrlPath + append;
	};

	/**
	 *
	 * @param options
	 * @returns {Promise<{headers: {Authorization: string}}>}
	 * @throws {authError}
	 */
	const buildAxiosConfig = async function( options = {} ) {
		let config = {
			...defaultOptions,
			...options
		};

		if(typeof(config.headers)==='undefined'){
			config.headers = {};
		}

		if(authorization) {
			let signaturesApiAccessToken = await store.dispatch( 'signaturesApiAuth/getAccessToken' );
			config.headers[ 'Authorization' ] = 'Bearer ' + signaturesApiAccessToken;
		}

		return config;
	}

	/**
	 * Standardize error reporting up the stack to our apiError
	 * @param {Error} e
	 * @throws {apiError}
	 */
	const apiErrorCatch = function( e ) {
		//api returned a structured error
		if( e.response && e.response.data && e.response.data.error ) {
			throw new apiError( e.response.data.message, e.response.status, e.response.data.data );
		}
		//axios error - presumably a network error
		else {
			throw new apiError( e.message, 0, e.stack );
		}
	}

	return {

		/**
		 *
		 * @returns {Promise<string>}
		 * @throws {authError}
		 */
		authenticate: async function() {

			//get the microsoft token that we need to exchange
			let microsoftAccessToken = await store.dispatch( 'microsoftAuth/getAccessToken' );

			//build the axios config
			let config = {
				...defaultOptions,
				headers: {
					'Authorization': 'Bearer ' + microsoftAccessToken
				}
			};

			//send the authentication request to the api
			try {
				let signaturesApiTokenResponse = await axios.get( urlBuild( 'auth/microsoft' ), config );
				return signaturesApiTokenResponse.data.accessToken;
			}
			catch( e ) {
				//api returned a structured error
				if( e.response ) {
					throw new authError( e.response.data.message, e.response.status, e.response.data.data );
				}
				//axios error - presumably a network error
				else {
					throw new authError( e.message );
				}
			}

		},

		/**
		 *
		 * @param {string} url
		 * @param {object} options
		 * @returns {Promise<AxiosResponse<any>>}
		 * @throws {authError}
		 */
		get: async function( url, options = {} ) {
			let config = await buildAxiosConfig( options );

			return axios.get( urlBuild( url ), config ).catch( apiErrorCatch );
		},

		/**
		 *
		 * @param {string} url
		 * @param {object} data
		 * @param {object} options
		 * @returns {Promise<AxiosResponse<any>>}
		 * @throws {authError}
		 */
		post: async function( url, data, options = {} ) {
			let config = await buildAxiosConfig( options );

			return axios.post( urlBuild( url ), data, config ).catch( apiErrorCatch );
		},

		/**
		 *
		 * @param {string} url
		 * @param {object} data
		 * @param {object} options
		 * @returns {Promise<AxiosResponse<any>>}
		 * @throws {authError}
		 */
		put: async function( url, data, options = {} ) {
			let config = await buildAxiosConfig( options );

			return axios.put( urlBuild( url ), data, config ).catch( apiErrorCatch );
		},

		/**
		 *
		 * @param {string} url
		 * @param {object} data
		 * @param {object} options
		 * @returns {Promise<AxiosResponse<any>>}
		 * @throws {authError}
		 */
		patch: async function( url, data, options = {} ) {
			let config = await buildAxiosConfig( options );

			return axios.patch( urlBuild( url ), data, config ).catch( apiErrorCatch );

		},

		/**
		 *
		 * @param {string} url
		 * @param {object} options
		 * @returns {Promise<AxiosResponse<any>>}
		 * @throws {authError}
		 */
		delete: async function( url, options = {} ) {
			let config = await buildAxiosConfig( options );

			return axios.delete( urlBuild( url ), config ).catch( apiErrorCatch );
		},

		/**
		 *
		 * @param {string} url
		 * @param {object} options
		 * @returns {Promise<AxiosResponse<any>>}
		 * @throws {authError}
		 */
		download: async function( url ) {
			let fileAccessToken = '';
			if(authorization) {
				try {
					let fileTokenApiResponse = await this.get( urlBuild( 'auth/fileToken' ) );
					fileAccessToken          = fileTokenApiResponse.data.accessToken;
				}
				catch(e) {
					throw new authError( 'The system failed to generate a file token. Try again.' );
				}
			}

			let downloadUrl = urlBuild(url);
			if(authorization) {
				let append = '?';
				if( downloadUrl.indexOf( '?' ) > -1 ) {
					append = '&';
				}
				downloadUrl += append + 'fileAccessToken=' + fileAccessToken;
			}

			window.open( downloadUrl)

		},

		/**
		 *
		 * @param {string} url
		 * @param {object} options
		 * @returns {Promise<AxiosResponse<any>>}
		 * @throws {authError}
		 */
		downloadUrl: async function( url ) {
			let fileAccessToken = '';
			if(authorization) {
				try {
					let fileTokenApiResponse = await this.get( urlBuild( 'auth/fileToken' ) );
					fileAccessToken          = fileTokenApiResponse.data.accessToken;
				}
				catch(e) {
					throw new authError( 'The system failed to generate a file token. Try again.' );
				}
			}

			let downloadUrl = urlBuild(url);

			if(authorization) {
				let append = '?';
				if( downloadUrl.indexOf( '?' ) > -1 ) {
					append = '&';
				}
				downloadUrl += append + 'fileAccessToken=' + fileAccessToken;
			}

			return downloadUrl;

		},

		createDocument:async function( file, templateId ) {
			let config = await buildAxiosConfig( {
				headers: {
					'Content-Type': 'multipart/form-data'
				}
			} );

			//add files to form data
			let formData = new FormData();
			formData.append('file', file);

			let url = 'document';

			if(typeof templateId == 'string') {
				url += '?templateId='+templateId;
			}

			return axios.post( urlBuild( url ), formData, config ).catch( apiErrorCatch );

		}

	};
};

export default apiService;